// /apps/patient/src/components/dashboard/MotivationCard.tsx

import { Flame, Zap, Target, Sparkles, Heart } from 'lucide-react';

interface MotivationCardProps {
  currentStreak: number;
  totalSessions: number;
  lastSessionDate: string | null;
  loading?: boolean;
}

// Message repository organized by user state
const MESSAGES = {
  // Strong streak (7+ days)
  strongStreak: [
    "Consistency beats perfection. Keep showing up.",
    "You're building something great. Don't stop now.",
    "This is becoming a habit. Well done.",
    "Your dedication is inspiring. Keep it up.",
    "Champions are made one workout at a time."
  ],
  // Building streak (3-6 days)
  buildingStreak: [
    "Momentum is on your side. Keep going.",
    "You're finding your rhythm. Nice work.",
    "Every workout counts. You're proving that.",
    "Building habits takes time. You're doing it.",
    "Three days becomes seven. Keep pushing."
  ],
  // Starting fresh (1-2 days)
  startingFresh: [
    "Day by day, step by step. You've got this.",
    "The journey of a thousand miles starts here.",
    "Small steps lead to big changes.",
    "You showed up. That's what matters most.",
    "One workout at a time. That's all it takes."
  ],
  // Returning (0 days, but has history)
  returning: [
    "Ready to restart? Your body remembers.",
    "Every day is a fresh start. Let's go.",
    "Welcome back. Your next workout awaits.",
    "The best time to start again is now.",
    "Breaks happen. What matters is coming back."
  ],
  // Brand new (0 days, no history)
  brandNew: [
    "Your fitness journey starts today.",
    "Welcome. Great things are ahead.",
    "The hardest part is starting. You're here.",
    "Every expert was once a beginner.",
    "Your future self will thank you."
  ]
};

function getStreakState(streak: number, totalSessions: number, lastSessionDate: string | null): keyof typeof MESSAGES {
  if (streak >= 7) return 'strongStreak';
  if (streak >= 3) return 'buildingStreak';
  if (streak >= 1) return 'startingFresh';
  if (totalSessions > 0 || lastSessionDate) return 'returning';
  return 'brandNew';
}

function getRandomMessage(state: keyof typeof MESSAGES): string {
  const messages = MESSAGES[state];
  // Use date-based selection for consistency within a day
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  const index = dayOfYear % messages.length;
  return messages[index];
}

function getStreakIcon(streak: number) {
  if (streak >= 7) return Flame;
  if (streak >= 3) return Zap;
  if (streak >= 1) return Target;
  return Sparkles;
}

function getStreakLabel(streak: number, totalSessions: number): string {
  if (streak >= 7) return `${streak}-day streak`;
  if (streak >= 3) return `Building momentum: ${streak} days`;
  if (streak === 2) return "Day 2 - let's go";
  if (streak === 1) return "Day 1 - great start";
  if (totalSessions > 0) return "Ready to restart?";
  return "Your journey begins";
}

function getStreakColor(streak: number): string {
  if (streak >= 7) return 'text-orange-500 bg-orange-50';
  if (streak >= 3) return 'text-yellow-500 bg-yellow-50';
  if (streak >= 1) return 'text-green-500 bg-green-50';
  return 'text-primary bg-primary/10';
}

export function MotivationCard({ currentStreak, totalSessions, lastSessionDate, loading }: MotivationCardProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 bg-gray-200 rounded-full" />
          <div className="h-5 w-32 bg-gray-200 rounded" />
        </div>
        <div className="h-4 w-48 bg-gray-200 rounded" />
      </div>
    );
  }

  const state = getStreakState(currentStreak, totalSessions, lastSessionDate);
  const message = getRandomMessage(state);
  const StreakIcon = getStreakIcon(currentStreak);
  const streakLabel = getStreakLabel(currentStreak, totalSessions);
  const colorClasses = getStreakColor(currentStreak);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2.5 rounded-full ${colorClasses}`}>
          <StreakIcon className="h-5 w-5" />
        </div>
        <span className={`font-semibold ${colorClasses.split(' ')[0]}`}>
          {streakLabel}
        </span>
      </div>
      <p className="text-gray-600 italic">"{message}"</p>
    </div>
  );
}
